// Text normalization utility for punctuation-insensitive phrase matching

/**
 * Normalizes text for punctuation-insensitive matching
 * - Unicode normalizes (handles special characters)
 * - Converts to lowercase
 * - Replaces smart quotes with regular quotes
 * - Removes most punctuation except apostrophes (preserves contractions)
 * - Collapses multiple whitespace into single spaces
 * - Trims leading/trailing whitespace
 */
export function normalizeText(text: string): string {
  if (!text || typeof text !== 'string') return '';
  
  return text
    // Unicode normalize to decomposed form
    .normalize('NFD')
    // Lowercase first
    .toLowerCase()
    // Replace ALL smart quote variations with regular quotes (comprehensive patterns)
    .replace(/[\u201C\u201D\u201E\u201F\u2033\u2036]/g, '"') // All double quote variants
    .replace(/[\u2018\u2019\u201A\u201B\u2032\u2035]/g, "'") // All single quote variants  
    // Replace other quotation marks
    .replace(/[\u00AB\u00BB\u2039\u203A]/g, '"') // Guillemets
    // Remove diacritical marks (from NFD normalization)
    .replace(/[\u0300-\u036f]/g, '')
    // Replace Unicode punctuation and symbols with spaces, preserve word chars, whitespace, and apostrophes
    .replace(/[^\p{L}\p{N}\s']/gu, ' ')
    // Collapse multiple whitespace into single spaces
    .replace(/\s+/g, ' ')
    // Trim
    .trim();
}

/**
 * Calculate confidence score based on normalized phrase length vs normalized text length
 * Used for 'contains' matching to provide meaningful confidence scores
 */
export function calculateContainsConfidence(
  normalizedPhrase: string, 
  normalizedText: string, 
  minConfidence: number = 60,
  maxConfidence: number = 100
): number {
  const phraseLength = normalizedPhrase.length;
  const textLength = normalizedText.length;
  
  if (phraseLength === 0 || textLength === 0) return 0;
  
  // Base confidence on phrase coverage of the text
  const coverage = phraseLength / textLength;
  const confidence = coverage * 200; // Scale factor
  
  return Math.min(maxConfidence, Math.max(minConfidence, confidence));
}